import type { Subject, SubjectField } from './api';

/** "Blok 4 · Les 7": the values of a lesson's own fields, in the order the teacher set the fields up. */
export function fieldSummary(subject: Subject | undefined, values: Record<string, string> | undefined): string {
  if (!subject || !values) return '';
  return subject.fields
    .map((field) => {
      const value = values[field.id];
      if (!value) return '';
      return field.kind === 'number' ? `${field.label} ${value}` : value;
    })
    .filter(Boolean)
    .join(' · ');
}

/** How many choice options a series may have before it falls back to a select. */
const MAX_CHOICE_SERIES = 12;
/** How many numbers a range may span to be shown as a series ("Les 1 … 20"). */
const MAX_NUMBER_SERIES = 20;

/** The full row a field is shown as ("Blok 1 ② 3 4"): options of a choice, or the numbers of a small range. */
export function seriesOf(field: SubjectField): string[] | null {
  if (field.kind === 'choice') {
    return field.options.length > 0 && field.options.length <= MAX_CHOICE_SERIES ? field.options : null;
  }
  if (field.kind === 'number') {
    const count = field.max - field.min + 1;
    if (count < 2 || count > MAX_NUMBER_SERIES) return null;
    return Array.from({ length: count }, (_, index) => String(field.min + index));
  }
  return null;
}

/** The value after the one used last time: numbers count on (within their range), choices and text repeat. */
function nextValue(field: SubjectField): string {
  if (!field.lastValue) return '';
  if (field.kind !== 'number') return field.lastValue;
  const next = Number(field.lastValue) + 1;
  return Number.isInteger(next) && next <= field.max ? String(next) : field.lastValue;
}

/** What to propose for a lesson that has no values yet; empty when there is nothing to propose. */
export function suggestion(subject: Subject, values: Record<string, string> | undefined): Record<string, string> {
  if (subject.fields.some((field) => values?.[field.id])) return {};
  return Object.fromEntries(subject.fields.map((field) => [field.id, nextValue(field)] as const).filter(([, value]) => value !== ''));
}
